/**
 * The guide's conditional question, answerable in place. A conditional flag
 * depends on an organizational fact the platform has no record of; whoever
 * knows the answer (the policy owner meeting the question, or compliance)
 * records Yes or No once. The answer is stored org-wide with provenance and
 * fed to every future guidance run as ground truth, so the same question is
 * never asked twice. Answers stay visible and can be changed.
 *
 * Pure add-on: with no stored answer it renders the question exactly as
 * before, plus the two buttons.
 */
import React, { useState } from 'react';

const API = process.env.REACT_APP_API_URL;

export default function ConditionAnswer({ condition, policyId, stored, compact = false }) {
  // stored: { answer, answered_by_name, answered_at } | undefined
  const [local, setLocal] = useState(null);   // becomes { answer } after a click
  const [changing, setChanging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const current = local || stored;

  const record = async (answer) => {
    setBusy(true); setError('');
    try {
      const d = await fetch(`${API}/api/ppm-review/conditions/answer`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ condition, answer, policy_id: policyId || null }),
      }).then(r => r.json());
      if (d.success) { setLocal({ answer, answered_by_name: 'you', answered_at: d.answered_at }); setChanging(false); }
      else setError(d.message || 'That did not go through.');
    } catch { setError('That did not go through.'); }
    setBusy(false);
  };

  const textSize = compact ? 'text-[11px]' : 'text-[12.5px]';
  const btn = `px-2.5 py-1 rounded-lg text-[11.5px] font-semibold ring-1 disabled:opacity-50`;

  if (current && !changing) {
    const when = current.answered_at
      ? new Date(current.answered_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric' }) : null;
    const yes = current.answer === 'yes';
    return (
      <div className={`mt-1.5 rounded-lg px-3 py-2 ring-1 ${yes ? 'bg-amber-50 ring-amber-200' : 'bg-slate-50 ring-slate-200'}`}>
        <p className={`${textSize} ${yes ? 'text-amber-900' : 'text-slate-600'}`}>
          {condition} <span className="font-semibold">Answered {yes ? 'Yes — this applies to your organization.' : 'No — you can disregard this item.'}</span>
          {(current.answered_by_name || when) && (
            <span className="text-slate-400"> ({[current.answered_by_name, when].filter(Boolean).join(', ')})</span>
          )}
        </p>
        <button type="button" onClick={() => setChanging(true)}
          className="mt-1 text-[11px] text-slate-500 underline underline-offset-2">Change answer</button>
      </div>
    );
  }

  return (
    <div className="mt-1.5 rounded-lg bg-amber-50 ring-1 ring-amber-200 px-3 py-2">
      {!compact && <p className="text-[11px] font-semibold uppercase tracking-[0.05em] text-amber-800">Answer this first — it decides whether this applies</p>}
      <p className={`${textSize} text-amber-900 ${compact ? '' : 'mt-0.5'}`}>{condition}</p>
      <div className="flex items-center gap-2 mt-1.5">
        <button type="button" disabled={busy} onClick={() => record('yes')}
          className={`${btn} bg-white ring-amber-300 text-amber-900 hover:bg-amber-100`}>Yes</button>
        <button type="button" disabled={busy} onClick={() => record('no')}
          className={`${btn} bg-white ring-slate-300 text-slate-700 hover:bg-slate-100`}>No</button>
        <span className="text-[10.5px] text-amber-800/70">Your answer is recorded and future reviews will use it.</span>
        {error && <span className="text-[11px] text-red-600">{error}</span>}
      </div>
    </div>
  );
}

/** Fetch the org's stored answers once; returns a Map keyed by condition text. */
export async function fetchConditionAnswers() {
  try {
    const d = await fetch(`${API}/api/ppm-review/conditions/answers`, { credentials: 'include' }).then(r => r.json());
    if (!d?.success) return new Map();
    return new Map((d.answers || []).map(a => [a.condition_text, a]));
  } catch { return new Map(); }
}
